import { TextureFunc } from "twgl.js";

/**
 * BlurRadius specifies how much bluring filter to apply by a given transformer.
 */
export enum BlurRadius {
    /**
     * Low bluring filtering (5px).
     */
    Low = "Low",

    /**
     * High bluring filtering (10px).
     */
    High = "High",
}

/**
 * Type of transformer to instantiate
 */
export enum BackgroundTransformerType {
    /**
     * Background blur
     */
    BackgroundBlur = "BackgroundBlur",

    /**
     * Background replace with an image
     */
    VirtualBackground = "VirtualBackground",

    /**
     * Background replace with an video
     */
    VideoBackground = "VideoBackground",

    /**
     * Silhouette blur
     */
    SilhouetteBlur = "SilhouetteBlur",
}

/**
 * MediaProcessorBaseConfig specifies the absolute URL for the path where the mediapipe assets used by the transformers from this library are stored.
 * Vonage hosts static assets of mediapipe that will be used in case this property is not set.
 * Vonage promises to take care of maintaining all versioning.
 * **WARNING**. By using this option the developer takes responsibility of the assets and their versioning.
 * *mediapipe selfie segmintaion got those assets:*
 * *selfie_segmentation_landscape.tflite*
 * *selfie_segmentation_solution_simd_wasm_bin.js*
 * *selfie_segmentation_solution_simd_wasm_bin.wasm*
 * *selfie_segmentation_solution_wasm_bin.js*
 * *selfie_segmentation_solution_wasm_bin.wasm*
 * *selfie_segmentation.binarypb*
 * *selfie_segmentation.tflite*
 * Please keep in mind that the assets list can change between versions.
 * All assets can be taken from `node_modules/@mediapipe/selfie_segmentation`
 */
export interface MediaProcessorBaseConfig {
    /**
     * Type of transformer
     */
    transformerType: BackgroundTransformerType;
    /**
     * A uri path to mediapipe assets.
     */
    mediapipeBaseAssetsUri?: string;
    /**
     * A uri path to tflite model.
     */
    modelAssetUriPath?: string;
    /**
     * Set of options about rendering transformed video frames.
     */
    renderingOptions?: RenderingOptions;
    /**
     * Set of options about obtaining the segmentation from video frames to transform.
     */
    segmentationOptions?: SegmentationOptions;
    /**
     * Applying a filter takes a brief moment during which the original track will be
     * bypassed. Activating this option will freeze the frame rather than show it.
     */
    avoidOriginalTrackLeak?: boolean;
}

export type RenderingOptions = Canvas2dOptions | WebglOptions;

/**
 * SegmentationOptions specifies the set of options about obtaining the segmentation from video frames to transform.
 */
export interface SegmentationOptions {
    /**
     * Whether to use the a light weight variant of the segmentation engine. Using this engine could have an impact in
     * the quality of segmentations.
     * Default value is false.
     */
    lightWeightEngine: boolean;
    /**
     * Whether to use GUP support for obtaining the segmentation from video frames to transform.
     * Not using GPU support might have some inpact in CUP consumption.
     * Default value is true.
     */
    gpuSupport: boolean;
}

/**
 * AssetBackgroundConfig specifies the absolute URL to the image or video file used for background replacement.
 */
export interface AssetBackgroundConfig {
    /**
     * URI for image or video asset.
     */
    backgroundAssetUri: string;
}

/**
 * BackgroundBlurConfig specifies the configuration for a transformer logic that performs background blurring.
 */
export interface BackgroundBlurConfig extends MediaProcessorBaseConfig {
    /**
     * Type of post process.
     */
    transformerType: BackgroundTransformerType.BackgroundBlur;
    /**
     * Blur strength, in px if provided as number
     */
    radius?: BlurRadius | number;
}

/**
 * VirtualBackgroundConfig specifies the configuration for a transformer logic that performs background replacement with a given image.
 */
export interface VirtualBackgroundConfig
    extends AssetBackgroundConfig,
        MediaProcessorBaseConfig {
    /**
     * Type of post process.
     */
    transformerType: BackgroundTransformerType.VirtualBackground;
}

/**
 * VirtualBackgroundConfig specifies the configuration for a transformer logic that performs background replacement with a given video.
 */
export interface VideoBackgroundConfig
    extends AssetBackgroundConfig,
        MediaProcessorBaseConfig {
    /**
     * Type of post process.
     */
    transformerType: BackgroundTransformerType.VideoBackground;
}

/**
 * SilhouetteBlurConfig specifies the configuration for a transformer logic that performs siluete blurring.
 */
export interface SilhouetteBlurConfig extends MediaProcessorBaseConfig {
    /**
     * Type of post process.
     */
    transformerType: BackgroundTransformerType.SilhouetteBlur;
    /**
     * Blur strength, in px if provided as number
     */
    radius?: BlurRadius | number;
}

/**
 * BackgroundOptions background options
 */
export type BackgroundOptions =
    | BackgroundBlurConfig
    | VirtualBackgroundConfig
    | VideoBackgroundConfig
    | SilhouetteBlurConfig;

/**
 * Valid data to feed webgl texture
 * @internal
 */
export type TextureSource =
    | string
    | number[]
    | ArrayBufferView
    | TexImageSource
    | TexImageSource[]
    | string[]
    | TextureFunc
    | undefined;

/**
 * Valid uniform data map
 * @internal
 */
export type UniformDataMap = {
    [key: string]: number | number[] | WebGLTexture;
};

/**
 * Feature support detail
 */
export interface FeatureSupport {
    /**
     * Is the feature supported
     */
    supported: boolean;

    /**
     * Readable message explaining the feature is not supported
     */
    message?: string;
}

/**
 * Features supported by the browser
 */
export interface SupportedBrowserFeatures {
    /**
     * Web assembly support, required to use ml-transformer
     */
    wasm: FeatureSupport;

    /**
     * Simd support, required to use ml-transformer
     */
    simd: FeatureSupport;

    /**
     * Insertable stream support, required to use ml-transformer
     */
    insertableStreams: FeatureSupport;

    /**
     * GPU processing support, optional
     */
    gpu: FeatureSupport;

    /**
     * Webgl context support, required
     */
    webgl: FeatureSupport;

    /**
     * Hardware support (whether the hardware underneath is powerful enough to run the library efficiently), optional.
     * If this flag is false, the cpu/gpu could end up overloaded producing lag/glitches.
     */
    performance: FeatureSupport;
}

/**
 * Selfie segmentation algorithm type
 * Fast can flickering but has low impact on performance
 * Precise avoid glitches and flickering, but is most costly.
 *
 * More explanations could be find here: https://vonage.github.io/ml-transformers-docs/docs/post-processing
 */
export enum WebglSelfieSegmentationType {
    /**
     * No selfie segmentation post-processing. It might produce flickering and has low impact on performance.
     */
    FAST = "FAST",
    /**
     * Enable selfie segmentation post-processing. It reduces flickering and glitches, however it might have some impact on performance.
     */
    PRECISE = "PRECISE",
}

export enum RenderingType {
    CANVAS = "CANVAS",
    WEBGL = "WEBGL",
}

export interface Canvas2dOptions {
    type: RenderingType.CANVAS;
}

export interface WebglOptions {
    type: RenderingType.WEBGL;
    selfieSegmentationType?: WebglSelfieSegmentationType;
}
